import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { Form } from '@/components/ui/form';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useSearchParams } from 'react-router-dom';
import {
  useUpdateCompanyElements,
  useGetCompanyData,
} from '@/hooks/use-company';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { toast } from '@/hooks/use-toast';
import ProjectElements from '@/components/ProjectElements';

// Form validation schema
const formSchema = z.object({
  selectedElements: z.array(z.string()),
});

type FormValues = z.infer<typeof formSchema>;

export default function Step4YourElements() {
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('orgId');
  const [selectedElements, setSelectedElements] = useState<string[]>([]);
  const [elementCategories, setElementCategories] = useState<
    Record<string, string>
  >({});

  // API hooks
  const updateElements = useUpdateCompanyElements();
  const { handleError } = useErrorHandler();

  // Fetch existing elements data
  const { data: companyData, isLoading: isLoadingElements } = useGetCompanyData(
    organizationId,
    'elements'
  );

  // Form setup
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      selectedElements: [],
    },
  });

  // Prefill form with existing elements
  useEffect(() => {
    if (companyData?.elements && companyData.elements.length > 0) {
      console.log(
        'Prefilling form with existing elements:',
        companyData.elements
      );

      // Extract element names from the API response
      const existingElements = companyData.elements.map(
        (elementItem: any) => elementItem.element.name
      );

      console.log('Mapped elements for form:', existingElements);

      setSelectedElements(existingElements);
      form.reset({
        selectedElements: existingElements,
      });
    }
  }, [companyData, form]);

  const handleNext = async (data: FormValues) => {
    console.log('Step 4 data:', data);
    console.log('Organization ID:', organizationId);

    if (!organizationId) {
      toast({
        title: 'Error',
        description: 'Organization ID is missing. Please try again.',
        variant: 'destructive',
      });
      return false; // Prevent navigation
    }

    try {
      // Convert selected elements back to categories format
      const categoryMap: Record<string, string[]> = {};

      // Group elements by their categories
      selectedElements.forEach((elementName) => {
        const category = elementCategories[elementName] || 'Other';
        if (!categoryMap[category]) {
          categoryMap[category] = [];
        }
        categoryMap[category].push(elementName);
      });

      // Convert to the required format
      const categories = Object.entries(categoryMap).map(
        ([category, elements]) => ({
          category,
          elements,
        })
      );

      const payload = {
        categories,
      };

      // Update company elements
      await updateElements.mutateAsync({
        companyId: organizationId,
        data: payload,
      });

      toast({
        title: 'Project Elements Updated',
        description: 'Your project elements have been saved successfully.',
        variant: 'success',
      });

      return true; // Allow navigation to the next step
    } catch (error: any) {
      console.error('Updating project elements failed:', error);
      handleError(error, {
        customMessage: 'Failed to update project elements. Please try again.',
      });
      return false; // Prevent navigation
    }
  };

  // Check for missing organizationId
  useEffect(() => {
    if (!organizationId) {
      toast({
        title: 'Missing Organization ID',
        description: 'Please complete the signup process first.',
        variant: 'warning',
      });
    }
  }, [organizationId]);

  // Debug: Log company data when it changes
  useEffect(() => {
    if (companyData) {
      console.log('Company elements data fetched:', companyData);
    }
  }, [companyData]);

  const handleElementToggle = (
    elementName: string,
    checked: boolean,
    category?: string
  ) => {
    setSelectedElements((prev) => {
      const updated = checked
        ? [...prev, elementName]
        : prev.filter((name) => name !== elementName);

      form.setValue('selectedElements', updated);
      return updated;
    });

    // Track the category for each element
    if (category && checked) {
      setElementCategories((prev) => ({
        ...prev,
        [elementName]: category,
      }));
    } else if (!checked) {
      setElementCategories((prev) => {
        const updated = { ...prev };
        delete updated[elementName];
        return updated;
      });
    }
  };

  return (
    <OnboardingLayout
      currentStep={4}
      totalSteps={5}
      onNext={async () => {
        const isValid = await form.trigger();
        if (!isValid) return false;

        // Wrap the form submission to ensure it always returns a boolean
        try {
          // Create a promise that resolves to the result of handleNext
          return new Promise((resolve) => {
            form.handleSubmit(async (data) => {
              const result = await handleNext(data);
              resolve(result !== false); // If handleNext returns false, return false; otherwise true
            })();
          });
        } catch (error) {
          console.error('Form submission error:', error);
          return false;
        }
      }}
      isLoading={updateElements.isPending}
    >
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">
            Select Your Project Elements
          </h2>
          <p className="text-muted-foreground">
            Select the features included in this project—such as decks, porches,
            fire features, or hardscapes—to customize forms and workflows
            accordingly.
          </p>
        </div>

        <Form {...form}>
          <div className="space-y-6">
            {/* Show loading state while fetching elements data */}
            {isLoadingElements && (
              <div className="flex items-center justify-center py-4">
                <div className="w-6 h-6 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin" />
                <span className="ml-2 text-muted-foreground">
                  Loading existing elements...
                </span>
              </div>
            )}

            {/* Project Elements Component */}
            <ProjectElements
              selectedElements={selectedElements}
              onElementToggle={handleElementToggle}
              className="p-4"
            />
          </div>
        </Form>
      </div>
    </OnboardingLayout>
  );
}
